import AddIcon from "@mui/icons-material/Add";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import Grid from "@mui/joy/Grid";
import Stack from "@mui/joy/Stack";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import { OmopDbReport } from "../../../models/analytics";
import { BatchBundle } from "../../../models/bundle";
import { Cohort, CohortId, InitCohort } from "../../../models/cohort";
import AnalyticsModal from "../../../shared/components/analytics/AnalyticsModal";
import TableSkeleton from "../../../shared/components/feedback/skeletons/TableSkeleton";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import {
  AdminQuartzClientContext,
  CohortQuartzClientContext,
  OmopQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { unixToDayjs } from "../../../shared/utils/time";
import { CohortList } from "../components/CohortList";
import NewCohortModal from "../components/NewCohortModal";

export const Cohorts = () => {
  const { showSnackbar } = useSnackbar();
  const omopQuartzClient = React.useContext(OmopQuartzClientContext);
  const cohortQuartzClient = React.useContext(CohortQuartzClientContext);
  const adminQuartzClient = React.useContext(AdminQuartzClientContext);
  const [openNewCohortModal, setOpenNewCohortModal] = React.useState(false);
  const [analyticsModal, setAnalyticsModal] = React.useState(false);
  const [refreshOmopLoading, setRefreshOmopLoading] = React.useState(false);
  const [cohorts, setCohorts] = React.useState<Cohort[] | null>(null);
  const [dbReport, setDbReport] = React.useState<OmopDbReport | null>(null);
  const [dbSize, setDbSize] = React.useState<number>(0);
  const navigate = useNavigate();

  const handleCreateCohort = async (name: string, description: string) => {
    const body = { name: name, description: description, db_name: "omop" } as InitCohort;
    try {
      const result = await cohortQuartzClient.postCohort(body);
      navigate(`/cohort/${result?.cohort_id}`);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const handleRefreshOmopDb = async () => {
    try {
      await cohortQuartzClient.refreshOmopData();
      showSnackbar({
        text: "Data refresh started. Try refreshing this page in a few minutes.",
        color: "success",
      });
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const getCohorts = React.useCallback(async () => {
    try {
      const result = await cohortQuartzClient.listCohorts();
      setCohorts(result);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  }, [cohortQuartzClient, showSnackbar]);

  const deleteCohort = async (ids: CohortId[]) => {
    const bundle = {
      entry: ids.map((id) => {
        return {
          request: {
            method: "DELETE",
            url: `Cohort/${id}`,
          },
        };
      }),
    } as BatchBundle;

    try {
      await adminQuartzClient.batchOperation(bundle);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
    getCohorts();
  };

  const getMainCohortStats = React.useCallback(async () => {
    setRefreshOmopLoading(true);
    try {
      const result = await omopQuartzClient.getOmopDbReport();
      setDbSize(result.number_of_patient);
      setDbReport(result);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setRefreshOmopLoading(false);
    }
  }, [omopQuartzClient, showSnackbar]);

  React.useEffect(() => {
    Promise.all([getCohorts(), getMainCohortStats()]);
  }, [getCohorts, getMainCohortStats]);

  return (
    <Box sx={{ p: 4, height: "94vh" }}>
      <NewCohortModal
        open={openNewCohortModal}
        setOpen={setOpenNewCohortModal}
        handleCreateCohort={handleCreateCohort}
      />
      <AnalyticsModal
        isOpen={analyticsModal}
        onClose={() => setAnalyticsModal(false)}
        dbReport={dbReport}
      />
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid xs={12}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography level="h2">
                <TitleStylizedUnderline color="rgb(81, 188, 81)">Cohorts</TitleStylizedUnderline>
                <Chip sx={{ ml: 2 }} startDecorator={<GroupOutlinedIcon fontSize="small" />}>
                  {dbSize} patient{dbSize >= 2 && "s"}
                </Chip>
                <Chip sx={{ ml: 2 }} startDecorator={<AutorenewIcon fontSize="small" />}>
                  Last update:{" "}
                  {unixToDayjs(dbReport?.last_updated)?.format("YYYY-MM-DD HH:mm") ?? "-"}
                </Chip>
              </Typography>
              <Typography>
                Cohorts are groups of patients that share common characteristics.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                disabled={dbSize === 0}
                startDecorator={<AddIcon />}
                onClick={() => {
                  setOpenNewCohortModal(true);
                }}
                color="primary"
              >
                Build a new cohort
              </Button>
              <Button
                onClick={() => {
                  setAnalyticsModal(true);
                }}
                disabled={dbSize === 0}
                startDecorator={<AnalyticsIcon />}
                color="primary"
              >
                Overall analytics
              </Button>
              <Tooltip
                title="All non-frozen cohort will be updated. It might take a while."
                placement="bottom-end"
              >
                <Button
                  onClick={() => {
                    handleRefreshOmopDb();
                  }}
                  startDecorator={refreshOmopLoading ? <CircularProgress /> : <AutorenewIcon />}
                  color="primary"
                  disabled={refreshOmopLoading}
                >
                  Refresh data
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>
        <Grid xs={12} sx={{ mt: 4 }}>
          {cohorts === null ? (
            <Box sx={{ height: "70vh", width: "100%", overflow: "hidden", position: "relative" }}>
              <TableSkeleton />
            </Box>
          ) : (
            <CohortList cohorts={cohorts} deleteCohort={deleteCohort} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
